import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  LayoutDashboard, Columns3, Megaphone, Share2, CalendarDays,
  MessagesSquare, RefreshCw, LogOut, Menu, X, Stethoscope, Users, Inbox, Syringe, BookOpen, KeyRound, Pill, Cake,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { canAccess, ROLE_LABELS } from "@/lib/roles";
import { NotificationBell } from "@/components/NotificationBell";
import { SyncStatusBadge } from "@/components/SyncStatusBadge";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard", section: "dashboard", end: true },
  { to: "/pipeline", label: "Lead Pipeline", icon: Columns3, testid: "sidebar-nav-lead-pipeline", section: "pipeline" },
  { to: "/campaigns", label: "Recall Campaigns", icon: Megaphone, testid: "sidebar-nav-recall-campaigns", section: "campaigns" },
  { to: "/referrals", label: "Referral Analytics", icon: Share2, testid: "sidebar-nav-referral-analytics", section: "referrals" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "sidebar-nav-calendar", section: "calendar" },
  { to: "/procedures", label: "Procedures", icon: Syringe, testid: "sidebar-nav-procedures", section: "procedures" },
  { to: "/communication", label: "Communication", icon: MessagesSquare, testid: "sidebar-nav-communication-center", section: "communication" },
  { to: "/medications", label: "Medications", icon: Pill, testid: "sidebar-nav-medications", section: "medications" },
  { to: "/birthdays", label: "Birthdays", icon: Cake, testid: "sidebar-nav-birthdays", section: "birthdays" },
  { to: "/drchrono", label: "DrChrono EMR", icon: RefreshCw, testid: "sidebar-nav-drchrono-sync", section: "drchrono" },
  { to: "/staff", label: "Staff & Roles", icon: Users, testid: "sidebar-nav-staff", section: "staff" },
  { to: "/messaging", label: "SMS Inbox", icon: Inbox, testid: "sidebar-nav-messaging", section: "messaging" },
];

function SidebarInner({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV.filter((i) => canAccess(user?.role, i.section));
  const isAdmin = canAccess(user?.role, "drchrono");
  const { data: emr } = useQuery({
    queryKey: ["drchrono-status"],
    queryFn: async () => (await api.get("/drchrono/status")).data,
    enabled: isAdmin,
    staleTime: 60000,
  });
  const emrConnected = emr?.connected;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-full flex-col justify-between bg-slate-950 text-slate-200">
      <div>
        <div className="px-5 py-5 border-b border-slate-800/80">
          <Logo />
        </div>
        {isAdmin && (
          <div className="mx-4 my-4 rounded-lg bg-slate-900/70 border border-slate-800 px-3 py-2.5 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`absolute inline-flex h-full w-full rounded-full ${emrConnected ? "bg-emerald-400 pulse-dot" : "bg-slate-500"}`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${emrConnected ? "bg-emerald-500" : "bg-slate-600"}`} />
            </span>
            <span className={`text-[11px] font-semibold flex items-center gap-1 ${emrConnected ? "text-emerald-300" : "text-slate-400"}`}>
              <Stethoscope className="h-3.5 w-3.5" /> {emrConnected ? "DrChrono EMR Connected" : "DrChrono EMR Not Connected"}
            </span>
          </div>
        )}
        <nav className="px-3 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-cyan-500/15 text-white border-l-2 border-cyan-400"
                    : "text-slate-400 hover:bg-slate-900 hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="p-4 border-t border-slate-800/80">
        <div className="flex items-center gap-3 mb-3 px-1">
          <div className="h-9 w-9 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-300 font-bold text-sm">
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-cyan-400/80 font-semibold truncate">{ROLE_LABELS[user?.role] || "Team Member"}</div>
          </div>
        </div>
        <a
          data-testid="sidebar-user-manual-link"
          href="/TheMensClinic_CRM_User_Manual.pdf"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          className="flex items-center w-full rounded-md px-4 h-9 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-900 transition-colors"
        >
          <BookOpen className="h-4 w-4 mr-2" /> User Manual
        </a>
        <button
          data-testid="sidebar-change-password-button"
          onClick={() => setPwOpen(true)}
          className="flex items-center w-full rounded-md px-4 h-9 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-900 transition-colors"
        >
          <KeyRound className="h-4 w-4 mr-2" /> Change password
        </button>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-900 h-9"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export function Layout({ title, subtitle, actions, children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 h-full"><SidebarInner onNavigate={() => setOpen(false)} /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-slate-700"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="min-w-0">
              <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 truncate">
                {title}
              </h1>
              {subtitle && <p className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SyncStatusBadge />
            {canAccess(user?.role, "communication") && <NotificationBell />}
            {actions}
          </div>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
        {user?.must_change_password && <ChangePasswordDialog open forced />}
      </div>
    </div>
  );
}
